import axios from "axios";

// ---------------------------------------------------------------------------
// Centralized API client
// All requests are cookie-credentialed (session auth managed by AuthContext).
// Base URL is configured via REACT_APP_API_URL env var (e.g. http://localhost:8080).
// ---------------------------------------------------------------------------

export const apiClient = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    withCredentials: true,
    headers: {
        "Content-Type": "application/json",
    },
});

// ---------------------------------------------------------------------------
// Profile / passenger endpoints
//   Backend collection equivalent: Firestore "profiles/{uid}"
// ---------------------------------------------------------------------------

/** Fetch the saved-passengers list for a user.
 *  Maps to: Firestore getDoc(doc(db, "profiles", uid))
 *  Backend: GET /profiles/:uid/passengers
 *  Expected response: { savedPassengers: PassengerData[] }
 */
export async function getSavedPassengers(uid: string): Promise<any[]> {
    const res = await apiClient.get(`/profiles/${uid}/passengers`);
    return res.data?.savedPassengers ?? [];
}

/** Persist the full saved-passengers list for a user (full replace / merge).
 *  Maps to: Firestore setDoc(doc(db, "profiles", uid), { savedPassengers }, { merge: true })
 *  Backend: PUT /profiles/:uid/passengers
 *  Body: { savedPassengers: PassengerData[] }
 */
export async function savePassengersToProfile(
    uid: string,
    savedPassengers: any[]
): Promise<void> {
    await apiClient.put(`/profiles/${uid}/passengers`, { savedPassengers });
}

/** Fetch the full user profile document.
 *  Maps to: Firestore getDoc(doc(db, "profiles", uid))
 *  Backend: GET /profiles/:uid
 *  Expected response: { customId?, role?, ... }
 */
export async function getUserProfile(uid: string): Promise<Record<string, any>> {
    const res = await apiClient.get(`/profiles/${uid}`);
    return res.data ?? {};
}

// ---------------------------------------------------------------------------
// Booking endpoints
//   Backend collection equivalent: Firestore "users/{uid}/bookings/{bookingId}"
// ---------------------------------------------------------------------------

/** Fetch a confirmed booking document.
 *  Maps to: Firestore getDoc(doc(db, `users/${uid}/bookings/${bookingId}`))
 *  Backend: GET /bookings/:uid/:bookingId
 *  Expected response: full booking document JSON
 */
export async function getBooking(
    uid: string,
    bookingId: string
): Promise<Record<string, any>> {
    const res = await apiClient.get(`/bookings/${uid}/${bookingId}`);
    return res.data ?? {};
}

// ---------------------------------------------------------------------------
// Cancel booking endpoint
//   Backend: POST /cancelBooking
// ---------------------------------------------------------------------------

export async function cancelBooking(payload: {
    pnr: string;
    status: "void" | "full" | "partial" | "refund";
    userId?: string;
    bookingId?: string;
    ticketNumbers?: string[];
    segmentNumbers?: number[];
    reason?: string;
    /** From requestCancelOtp. Required for staff once cancellation OTP is on. */
    challengeId?: string;
    code?: string;
}): Promise<any> {
    const res = await apiClient.post("/cancelBooking", payload);
    return res.data;
}

/**
 * The code that has to be presented before a void, cancellation or refund.
 *
 * `otpRequired: false` is the normal answer for a customer cancelling their own
 * booking, and for every caller while the server's CANCEL_OTP_ENABLED is off —
 * so the caller proceeds straight to cancelBooking rather than treating it as a
 * failure.
 */
export interface CancelOtpChallenge {
    otpRequired: boolean;
    challengeId?: string;
    /** Masked, e.g. "•••• 6301" — safe to show on screen. */
    sentTo?: string;
    /** What the code approves, e.g. "a void of AED 855.00". */
    amount?: string;
    resendInSeconds?: number;
    sendCount?: number;
}

/**
 * Ask for the code for ONE specific cancellation.
 *
 * The action fields must match the cancelBooking call exactly. The server
 * fingerprints them, so a code issued to cancel one segment cannot be spent
 * refunding the whole ticket — change the action and the code stops working.
 */
export async function requestCancelOtp(payload: {
    bookingId: string;
    status: "void" | "full" | "partial" | "refund";
    ticketNumbers?: string[];
    segmentNumbers?: number[];
}): Promise<CancelOtpChallenge> {
    const res = await apiClient.post("/cancelBooking/otp", payload);
    return res.data;
}

/** Send the same code again. The action has to match the original request. */
export async function resendCancelOtp(payload: {
    bookingId: string;
    status: "void" | "full" | "partial" | "refund";
    ticketNumbers?: string[];
    segmentNumbers?: number[];
    challengeId: string;
}): Promise<CancelOtpChallenge> {
    const res = await apiClient.post("/cancelBooking/otp/resend", payload);
    return res.data;
}

// ---------------------------------------------------------------------------
// Sales Reports endpoint
//   Backend: POST /reports/daily-sales
// ---------------------------------------------------------------------------

export async function getDailySalesReport(
    date: string,
    endDate?: string,
    pcc?: string
): Promise<any> {
    const res = await apiClient.post("/reports/daily-sales", {
        date,
        ...(endDate ? { endDate } : {}),
        ...(pcc ? { pcc } : {}),
    });
    return res.data;
}

// ---------------------------------------------------------------------------
// PNR Audit Trail
//   Backend: POST /reports/audit-trail
// ---------------------------------------------------------------------------

export async function getAuditTrail(pnrLocator: string): Promise<any> {
    const res = await apiClient.post("/reports/audit-trail", { pnrLocator });
    return res.data;
}

// ---------------------------------------------------------------------------
// ASR Reports (SabreSonic airline customers)
//   Require SABRE_ASR_SOAP_ENDPOINT env var on the server.
// ---------------------------------------------------------------------------

export async function getAsrSalesSummary(payload: {
    ticketingProvider: string;
    stationNumber: string;
    employeeNumber: string;
    reportDate: string;
    currencyCodes?: string[];
    fopCodes?: string[];
    documentTypes?: string[];
}): Promise<any> {
    const res = await apiClient.post("/reports/asr/sales-summary", payload);
    return res.data;
}

export async function getAsrStationSummary(payload: {
    ticketingProvider: string;
    stationNumber: string;
    reportDate: string;
}): Promise<any> {
    const res = await apiClient.post("/reports/asr/station-summary", payload);
    return res.data;
}

export async function getAsrAgentSummary(payload: {
    ticketingProvider: string;
    employeeNumber: string;
    reportDate: string;
    stationNumber?: string;
}): Promise<any> {
    const res = await apiClient.post("/reports/asr/agent-summary", payload);
    return res.data;
}

export async function getAsrAccounting(payload: {
    ticketingProvider: string;
    stationNumber: string;
    employeeNumber: string;
    reportDate: string;
    operation?: string;
    remark?: string;
}): Promise<any> {
    const res = await apiClient.post("/reports/asr/accounting", payload);
    return res.data;
}

export async function getAsrDocumentDetail(payload: {
    ticketingProvider: string;
    documentNumber: string;
}): Promise<any> {
    const res = await apiClient.post("/reports/asr/document-detail", payload);
    return res.data;
}

export async function getAsrCreditCard(payload: {
    ticketingProvider: string;
    stationNumber: string;
    employeeNumber: string;
    reportDate: string;
}): Promise<any> {
    const res = await apiClient.post("/reports/asr/credit-card", payload);
    return res.data;
}

// ---------------------------------------------------------------------------
// Seat Map endpoint
//   Backend: POST /flights/seat-map
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Exchange booking endpoints
//   Backend: POST /flights/exchange/search, POST /flights/exchange/book
// ---------------------------------------------------------------------------

export async function searchExchangeOffers(payload: {
    pnr: string;
    newDepartureDate: string;
    newOrigin?: string;
    newDestination?: string;
}): Promise<any> {
    const res = await apiClient.post("/flights/exchange/search", payload);
    return res.data;
}

export async function executeExchange(payload: {
    pnr: string;
    selectedOffer: any;
    formOfPayment?: string;
}): Promise<any> {
    const res = await apiClient.post("/flights/exchange/book", payload);
    return res.data;
}

// ---------------------------------------------------------------------------
// Seat Map endpoint
//   Backend: POST /flights/seat-map
// ---------------------------------------------------------------------------

export async function getSeatMap(payload: {
    userId: string;
    revalId: string;
    sessionId?: string;
    passengerCounts: { ADT?: number; CHD?: number; INF?: number };
    currency?: string;
}): Promise<any> {
    const res = await apiClient.post("/flights/seat-map", payload);
    return res.data;
}

// ---------------------------------------------------------------------------
// Fare Calendar endpoints
// ---------------------------------------------------------------------------

export async function getLeadPriceCalendar(payload: {
    origin: string;
    destination: string;
    departureDate: string;
    returnDate?: string;
    lengthOfStay?: number;
}): Promise<any> {
    const res = await apiClient.post("/flights/lead-price-calendar", payload);
    return res.data;
}

export async function getInstaFlights(payload: {
    origin: string;
    destination: string;
    departureDate: string;
    returnDate?: string;
    maxStops?: number;
    limit?: number;
    sortBy?: string;
}): Promise<any> {
    const res = await apiClient.post("/flights/instaflights", payload);
    return res.data;
}

// ---------------------------------------------------------------------------
// Pre-booking checklist
// ---------------------------------------------------------------------------

export interface ChecklistItem {
    id: number;
    code: string;
    title: string;
    description: string;
    category: "visa" | "insurance" | "service";
    price: number;
    currency: string;
    isRecommended: boolean;
    priority: number;
}

export async function getPreBookingChecklist(destinationCode?: string): Promise<ChecklistItem[]> {
    const res = await apiClient.get("/flights/checklist", {
        params: destinationCode ? { destination: destinationCode } : undefined,
    });
    return res.data?.items ?? [];
}

// ---------------------------------------------------------------------------
// Markup Rules (admin/internal_staff only)
// ---------------------------------------------------------------------------

export async function getMarkupRules(): Promise<any> {
    const res = await apiClient.get("/api/markup");
    return res.data;
}

export async function createMarkupRule(data: any): Promise<any> {
    const res = await apiClient.post("/api/markup", data);
    return res.data;
}

export async function updateMarkupRule(id: number, data: any): Promise<any> {
    const res = await apiClient.put(`/api/markup/${id}`, data);
    return res.data;
}

export async function deleteMarkupRule(id: number): Promise<any> {
    const res = await apiClient.delete(`/api/markup/${id}`);
    return res.data;
}

// ---------------------------------------------------------------------------
// Homepage content CMS — Explore blocks + Article cards (admin/internal_staff)
// ---------------------------------------------------------------------------

// ── Public reads (used by the homepage components) ──
export async function getExploreContent(): Promise<any> {
    const res = await apiClient.get("/content/explore");
    return res.data;
}

export async function getArticlesContent(): Promise<any> {
    const res = await apiClient.get("/content/articles");
    return res.data;
}

// ── Admin: Explore blocks ──
export async function getContentBlocks(): Promise<any> {
    const res = await apiClient.get("/content/admin/blocks");
    return res.data;
}

export async function createContentBlock(data: any): Promise<any> {
    const res = await apiClient.post("/content/admin/blocks", data);
    return res.data;
}

export async function updateContentBlock(id: number, data: any): Promise<any> {
    const res = await apiClient.put(`/content/admin/blocks/${id}`, data);
    return res.data;
}

export async function deleteContentBlock(id: number): Promise<any> {
    const res = await apiClient.delete(`/content/admin/blocks/${id}`);
    return res.data;
}

// ── Admin: Articles ──
export async function getContentArticles(): Promise<any> {
    const res = await apiClient.get("/content/admin/articles");
    return res.data;
}

export async function createContentArticle(data: any): Promise<any> {
    const res = await apiClient.post("/content/admin/articles", data);
    return res.data;
}

export async function updateContentArticle(id: number, data: any): Promise<any> {
    const res = await apiClient.put(`/content/admin/articles/${id}`, data);
    return res.data;
}

export async function deleteContentArticle(id: number): Promise<any> {
    const res = await apiClient.delete(`/content/admin/articles/${id}`);
    return res.data;
}

// ── Admin: reorder (drag-and-drop) ──
export async function reorderContent(
    type: "blocks" | "articles",
    items: { id: number; sortOrder: number }[],
): Promise<any> {
    const res = await apiClient.post("/content/admin/reorder", { type, items });
    return res.data;
}

// ── Admin: media upload (presign → direct PUT to S3) ──
export interface UploadedMedia {
    key: string;
    publicUrl: string;
    mediaType: "image" | "video";
}

/**
 * Upload an image or video to S3 in two steps:
 *   1. ask the server for a presigned PUT URL + the stable public URL,
 *   2. PUT the file bytes straight to S3.
 * Returns the S3 key + public URL to store on a block/article.
 *
 * NOTE: the S3 PUT uses raw fetch (not apiClient) and sets ONLY Content-Type —
 * Content-Length is a forbidden header in the browser and would break the
 * request signature.
 */
export async function uploadContentMedia(file: File): Promise<UploadedMedia> {
    const presign = await apiClient.post("/content/admin/upload", {
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
    });
    const { uploadUrl, key, publicUrl, mediaType } = presign.data;

    const put = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
    });
    if (!put.ok) {
        throw new Error("Failed to upload file to S3");
    }

    return { key, publicUrl, mediaType };
}

// ---------------------------------------------------------------------------
// Staff lifecycle — approval queue and the on/off switch (super user only)
// Backend: /admin/staff*
// ---------------------------------------------------------------------------

export type StaffStatus = "none" | "pending" | "approved" | "rejected" | "disabled";

export interface StaffSummary {
    uid: string;
    email: string;
    displayName: string;
    mobile: string;
    officeId?: string | null;
    staffStatus: StaffStatus;
    effectiveRole: string;
    staffStatusChangedBy?: string | null;
    staffStatusChangedAt?: string | null;
    emailVerified: boolean;
    createdAt: string;
    /** Branches this person may work at. */
    assignedBranchIds?: string[];
}

/** Everyone in the staff lifecycle, pending first. `status` filters to one state. */
export async function getStaff(status?: StaffStatus | "all"): Promise<StaffSummary[]> {
    const res = await apiClient.get("/api/admin/staff", {
        params: status && status !== "all" ? { status } : undefined,
    });
    return res.data?.staff ?? [];
}

/** Just the queue — used for the count badge. */
export async function getPendingStaff(): Promise<{ pending: StaffSummary[]; count: number }> {
    const res = await apiClient.get("/api/admin/staff/pending");
    return { pending: res.data?.pending ?? [], count: res.data?.count ?? 0 };
}

export async function approveStaff(uid: string): Promise<StaffSummary> {
    const res = await apiClient.post(`/api/admin/staff/${uid}/approve`);
    return res.data?.staff;
}

export async function rejectStaff(uid: string): Promise<StaffSummary> {
    const res = await apiClient.post(`/api/admin/staff/${uid}/reject`);
    return res.data?.staff;
}

export async function disableStaff(uid: string): Promise<StaffSummary> {
    const res = await apiClient.post(`/api/admin/staff/${uid}/disable`);
    return res.data?.staff;
}

/**
 * Set a staff member's mobile number. Super user only.
 *
 * Staff cannot change their own — the OTP is delivered there, so this is both
 * the correction path and the way somebody locked out by a wrong number gets
 * back in.
 */
export async function setStaffMobile(uid: string, mobile: string): Promise<void> {
    await apiClient.put(`/api/admin/users/${uid}`, { mobile });
}

// ---------------------------------------------------------------------------
// Branch master (offices). Reading is open to staff; changing is super user only.
// Backend: /admin/offices
// ---------------------------------------------------------------------------

export interface Branch {
    officeId: string;
    name: string;
    tenantId: string;
    /** HEAD OFFICE. Cannot be deleted. */
    isProtected: boolean;
    /** Where new accounts start. */
    isDefault: boolean;
    createdAt: string;
}

export async function getBranches(): Promise<Branch[]> {
    const res = await apiClient.get("/api/admin/offices");
    return res.data?.branches ?? [];
}

export async function createBranch(name: string): Promise<Branch> {
    const res = await apiClient.post("/api/admin/offices", { name });
    return res.data?.branch;
}

export async function renameBranch(officeId: string, name: string): Promise<Branch> {
    const res = await apiClient.put(`/api/admin/offices/${officeId}`, { name });
    return res.data?.branch;
}

export async function deleteBranch(officeId: string): Promise<void> {
    await apiClient.delete(`/api/admin/offices/${officeId}`);
}

/** Move a staff member to a different branch. Super user only. */
export async function setStaffBranch(uid: string, officeId: string): Promise<void> {
    await apiClient.put(`/api/admin/users/${uid}`, { officeId });
}

/**
 * Record which branch this staff member is working at today.
 *
 * Every booking they make is stamped with it, so the branch sales figures
 * follow from this call. Asked after each sign-in, because staff rotate.
 */
export async function setWorkingBranch(officeId: string): Promise<{ officeId: string; name: string }> {
    const res = await apiClient.post("/profile/branch", { officeId });
    return res.data?.branch;
}

/**
 * The branches the signed-in user may choose from.
 *
 * Ordinary staff get only what they are assigned to; super users and IT get
 * every branch. Not the same as getBranches(), which is the full master list
 * for administration.
 */
export async function getMyBranches(): Promise<{ branches: Branch[]; current: string | null }> {
    const res = await apiClient.get("/profile/branches");
    return { branches: res.data?.branches ?? [], current: res.data?.current ?? null };
}

/** Replace which branches a staff member may work at. Super user only. */
export async function setStaffBranches(uid: string, officeIds: string[]): Promise<string[]> {
    const res = await apiClient.put(`/api/admin/staff/${uid}/branches`, { officeIds });
    return res.data?.assignedBranchIds ?? officeIds;
}

// ---------------------------------------------------------------------------
// Daily sales limit
// Backend: /profile/limit (own usage), /admin/limit (settings)
// ---------------------------------------------------------------------------

export interface LimitUsage {
    /** False for super users and IT, who are not subject to the limit. */
    limited: boolean;
    used: number;
    dailyLimit: number;
    warnAt: number;
    currency: string;
    remaining: number;
    warning: boolean;
    blocked: boolean;
    /** Midnight Dubai time. */
    resetsAt: string;
}

export interface LimitSettings {
    dailyLimit: number;
    warnAt: number;
    currency: string;
    updatedBy: string | null;
    updatedAt: string;
}

/** Where the signed-in person stands today. */
export async function getMyLimit(): Promise<LimitUsage> {
    const res = await apiClient.get("/profile/limit");
    return res.data;
}

export async function getLimitSettings(): Promise<LimitSettings> {
    const res = await apiClient.get("/api/admin/limit");
    return res.data?.settings;
}

/** Change the limit for everybody. Super user only. */
export async function updateLimitSettings(dailyLimit: number, warnAt: number): Promise<LimitSettings> {
    const res = await apiClient.put("/api/admin/limit", { dailyLimit, warnAt });
    return res.data?.settings;
}

/** Clear one person's used amount for today. Super user only. */
export async function resetStaffLimit(uid: string, reason?: string): Promise<{ usedBefore: number; usedAfter: number }> {
    const res = await apiClient.post(`/api/admin/staff/${uid}/limit-reset`, { reason });
    return res.data;
}

// ---------------------------------------------------------------------------
// The code before a cash sale
// Backend: /payments/cash/* (the agent), /admin/approvals (super users)
// ---------------------------------------------------------------------------

export interface PaymentOtpChallenge {
    /** False when the payment code is switched off — carry on without one. */
    otpRequired: boolean;
    challengeId?: string;
    /** The agent's own number, masked. */
    sentTo?: string;
    /** What they are approving, e.g. "AED 1,200". */
    amount?: string;
    resendInSeconds?: number;
    sendCount?: number;
    /** True once the code has been sent enough times to ask head office. */
    canEscalate?: boolean;
}

export interface CashAuthRequest {
    id: number;
    bookingId: string;
    uid: string;
    amount: number | null;
    currency: string;
    reason: string | null;
    status: "pending" | "approved" | "declined";
    requestedAt: string;
    expiresAt: string;
    decidedBy: string | null;
    decidedAt: string | null;
    /** Attached when the queue is listed, so a person is deciding about a person. */
    agentName?: string | null;
    agentEmail?: string | null;
}

/**
 * What became of a request. Derived by the server rather than stored.
 *
 * `expired` does not exist in the database — a request nobody answered keeps
 * `status = 'pending'` with an expiry in the past. Reading it as "still waiting"
 * would be wrong months later, so the server names the timeout for what it is.
 */
export type CashAuthOutcome = "approved" | "declined" | "expired" | "waiting";

export interface CashAuthHistoryRow extends CashAuthRequest {
    outcome: CashAuthOutcome;
    /** The super user who decided it, by name — `decidedBy` is only a uid. */
    decidedByName?: string | null;
}

export interface CashAuthHistoryPage {
    requests: CashAuthHistoryRow[];
    total: number;
    limit: number;
    offset: number;
}

/**
 * The e-ticket PDF for one booking, as a blob to save.
 *
 * The same document that is emailed and sent over WhatsApp — rendered by the
 * server from one template, so a printed copy and an emailed copy can never
 * disagree. Fetched through `apiClient` rather than pointed at with a link,
 * because the route reads the session cookie and answers 404 for a booking that
 * is not the caller's.
 */
export async function downloadTicketPdf(bookingId: string): Promise<Blob> {
    const res = await apiClient.get(
        `/documents/ticket.pdf?bookingId=${encodeURIComponent(bookingId)}`,
        { responseType: "blob" },
    );
    return res.data as Blob;
}

// ---------------------------------------------------------------------------
// Operational settings
//   Backend: GET/PUT /admin/settings — `settings` module, manage to change.
// ---------------------------------------------------------------------------

export interface AppSettings {
    /** How long an unpaid booking is held before it expires, in minutes. */
    paymentWindowMinutes: number;
    /**
     * The share of a fare's service fee an agent may take off it, 0–100.
     *
     * 100 means the whole fee, which is the rule as agreed; 0 switches
     * discounts off. The fee itself is an absolute ceiling underneath this and
     * is not configurable — a sale can never go below what the airline is owed.
     */
    maxDiscountPercentOfFee: number;
    updatedBy: string | null;
    updatedAt: string | null;
}

export interface AppSettingsPage {
    settings: AppSettings;
    /** The bounds the server will hold a value to, so the form can say them. */
    limits: {
        paymentWindowMinutes: { min: number; max: number };
        maxDiscountPercentOfFee: { min: number; max: number };
    };
}

export async function getAppSettings(): Promise<AppSettingsPage> {
    const res = await apiClient.get("/api/admin/settings");
    return res.data;
}

/**
 * Save one or more settings. A field left out keeps whatever is stored, so the
 * screen can save the box somebody actually edited without having to send —
 * and risk clobbering — the others.
 */
export async function updateAppSettings(input: {
    paymentWindowMinutes?: number;
    maxDiscountPercentOfFee?: number;
}): Promise<AppSettings> {
    const res = await apiClient.put("/api/admin/settings", input);
    return res.data?.settings;
}

// ---------------------------------------------------------------------------
// Taking an amount off a fare
// Backend: GET /discounts/cap
// ---------------------------------------------------------------------------

/** Why there is no discount box on this fare. Null when there is one. */
export type DiscountUnavailable = "not_staff" | "expired" | "no_fee" | "switched_off";

export interface DiscountQuote {
    allowed: boolean;
    unavailable: DiscountUnavailable | null;
    /** The agency's gross fee on this fare. */
    serviceFee: number;
    /** The share of it head office permits, 0–100. */
    percentOfFee: number;
    /** The most that may actually come off. */
    maxDiscount: number;
    currency: string;
}

/**
 * The ceiling on what may be taken off the fare about to be sold.
 *
 * Asked of the server rather than worked out here, and that is the whole point.
 * The previous discount feature computed its own cap from the itinerary the
 * browser was holding, reached the wrong fare's service fee when a tier had
 * been chosen, and offered agents a discount the server then refused. This asks
 * the server about the exact `revalId` that will be confirmed, so the number on
 * screen is the number that will be enforced.
 */
export async function getDiscountCap(revalId: string): Promise<DiscountQuote> {
    const res = await apiClient.get("/discounts/cap", { params: { revalId } });
    return res.data;
}

// ---------------------------------------------------------------------------
// Special service requests
// Backend: GET /ssr/catalogue
// ---------------------------------------------------------------------------

export interface SsrOption { code: string; label: string }

export interface SsrCatalogue {
    meal?: SsrOption[];
    assistance?: SsrOption[];
    seat_preference?: SsrOption[];
    contact?: SsrOption[];
}

/**
 * What a passenger may ask the airline for.
 *
 * Fetched rather than hardcoded. Ticket.tsx already carries its own copy of the
 * meal list and it has drifted four codes behind — a second copy on the
 * passenger form would be a second thing to forget. The list is static, so one
 * fetch per session is plenty.
 */
export async function getSsrCatalogue(): Promise<SsrCatalogue> {
    const res = await apiClient.get("/ssr/catalogue");
    return res.data?.categories ?? {};
}

export interface ExistingSsr {
    code: string;
    label?: string | null;
    /** NN pending · KK/HK confirmed · UC/UN refused. */
    status?: string | null;
    statusLabel?: string | null;
    text?: string | null;
    /** 1-based traveller numbers. Empty for airline notices. */
    travellers: number[];
    /** 1-based segment numbers. Empty when it covers the whole journey. */
    segments: number[];
}

export interface BookingTraveller { nameNumber: string; number: number; name: string }
export interface BookingSegment {
    number: number; from: string; to: string;
    airline: string; flightNumber: string; date: string;
}

/** What the airline already holds for a booking, and what it made of it. */
export async function getBookingSsrs(bookingId: string): Promise<{
    pnr: string;
    services: ExistingSsr[];
    travellers: BookingTraveller[];
    segments: BookingSegment[];
}> {
    const res = await apiClient.get(`/bookings/${encodeURIComponent(bookingId)}/ssr`);
    return res.data;
}

/**
 * Ask the airline for something on a booking that already has a PNR.
 *
 * Codes already present are skipped server-side and named back in `skipped` —
 * Sabre refuses the whole request on a duplicate, so sending one would take the
 * others down with it.
 */
export async function addBookingSsrs(
    bookingId: string,
    ssrs: Array<{ code: string; nameNumber?: string; segmentNumbers?: string[] }>,
): Promise<{ added: string[]; skipped: string[]; message: string }> {
    const res = await apiClient.post(`/bookings/${encodeURIComponent(bookingId)}/ssr`, { ssrs });
    return res.data;
}

/**
 * Record a frequent flyer number on a booking.
 *
 * Separate from the SSR call because it is a different mechanism — there is no
 * API element for it, so the server puts it in as the `FF` host entry. Sabre
 * validates the number with the airline as it goes, so a rejection here is
 * worth showing verbatim: "NAME DOES NOT MATCH FREQUENT TRAVELER NUMBER OWNER"
 * is the difference between a typo and the wrong passenger.
 */
export async function addFrequentFlyer(
    bookingId: string,
    carrier: string,
    number: string,
    nameNumber?: string,
): Promise<{ carrier: string; number: string; message: string }> {
    const res = await apiClient.post(
        `/bookings/${encodeURIComponent(bookingId)}/frequent-flyer`,
        { carrier, number, nameNumber },
    );
    return res.data;
}

/** Per-channel outcome of a resend. `skipped` means there was nothing to send to. */
export interface TicketSendChannel {
    ok: boolean;
    to?: string;
    error?: string;
    skipped?: boolean;
}

export interface TicketSendResult {
    email: TicketSendChannel;
    whatsapp: TicketSendChannel;
    pdf: { ok: boolean; error?: string };
}

/**
 * Send the e-ticket to the passenger again.
 *
 * Recipients come from the booking on the server — never from here — so this
 * cannot be pointed at another address to extract somebody's ticket.
 *
 * @param channel omit for both, or name one to send only that.
 */
export async function sendTicketToPassenger(
    bookingId: string,
    channel?: "email" | "whatsapp",
): Promise<TicketSendResult> {
    const res = await apiClient.post("/documents/ticket/send", {
        bookingId,
        ...(channel ? { channel } : {}),
    });
    return res.data;
}

/** Send the code for this sale to the agent's phone. */
export async function requestPaymentOtp(bookingId: string): Promise<PaymentOtpChallenge> {
    const res = await apiClient.post("/payments/cash/otp", { bookingId });
    return res.data;
}

export async function resendPaymentOtp(bookingId: string, challengeId: string): Promise<PaymentOtpChallenge> {
    const res = await apiClient.post("/payments/cash/otp/resend", { bookingId, challengeId });
    return res.data;
}

/**
 * Approve the sale with the code.
 *
 * A wrong code comes back as `ok: false` with something to show the agent, not
 * as an error — only a genuine problem (over the limit, somebody else's
 * booking) throws.
 */
export async function authorisePaymentOtp(
    bookingId: string, challengeId: string, code: string,
): Promise<{ ok: boolean; message?: string; attemptsLeft?: number }> {
    const res = await apiClient.post("/payments/cash/authorise", { bookingId, challengeId, code });
    return res.data;
}

/** Ask head office to approve this sale when the code will not arrive. */
export async function requestCashApproval(
    bookingId: string, reason?: string,
): Promise<{ request: CashAuthRequest; alreadyWaiting: boolean }> {
    const res = await apiClient.post("/payments/cash/request-approval", { bookingId, reason });
    return res.data;
}

/** Has head office approved it yet? */
export async function checkCashApproval(
    bookingId: string,
): Promise<{ approved: boolean; status?: string; expiresAt?: string | null }> {
    const res = await apiClient.get(`/payments/cash/approval/${encodeURIComponent(bookingId)}`);
    return res.data;
}

/** Everything waiting on a super user. */
export async function getApprovalQueue(): Promise<CashAuthRequest[]> {
    const res = await apiClient.get("/api/admin/approvals");
    return res.data?.requests ?? [];
}

/**
 * Every request ever made, newest first — the queue only ever shows live ones.
 *
 * `from`/`to` are ISO dates matched against when the request was *made*, so a
 * day's page holds what was asked that day regardless of when it was answered.
 */
export async function getApprovalHistory(filter: {
    outcome?: CashAuthOutcome;
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
} = {}): Promise<CashAuthHistoryPage> {
    const params = new URLSearchParams();
    if (filter.outcome) params.set("outcome", filter.outcome);
    if (filter.from) params.set("from", filter.from);
    if (filter.to) params.set("to", filter.to);
    if (filter.limit !== undefined) params.set("limit", String(filter.limit));
    if (filter.offset !== undefined) params.set("offset", String(filter.offset));

    const qs = params.toString();
    const res = await apiClient.get(`/api/admin/approvals/history${qs ? `?${qs}` : ""}`);
    return {
        requests: res.data?.requests ?? [],
        total: res.data?.total ?? 0,
        limit: res.data?.limit ?? 50,
        offset: res.data?.offset ?? 0,
    };
}

export async function approveCashRequest(id: number): Promise<CashAuthRequest> {
    const res = await apiClient.post(`/api/admin/approvals/${id}/approve`);
    return res.data?.request;
}

export async function declineCashRequest(id: number): Promise<CashAuthRequest> {
    const res = await apiClient.post(`/api/admin/approvals/${id}/decline`);
    return res.data?.request;
}

// ---------------------------------------------------------------------------
// The super user dashboard
// Backend: /admin/dashboard, /admin/dashboard/staff[/:uid]
// ---------------------------------------------------------------------------

export type DashboardPeriodName = "day" | "week" | "month" | "year" | "custom";

export interface DashboardPeriod {
    name: DashboardPeriodName;
    /** Inclusive Dubai dates. */
    from: string;
    to: string;
    label: string;
}

export interface SalesTotals {
    sales: number;
    bookings: number;
    passengers: number;
    /** Kept apart from sales, never subtracted from them. */
    refunded: number;
    refundedBookings: number;
    pending: number;
    pendingBookings: number;
    failedBookings: number;
    /** What those failures are holding: paid for, no ticket. Kept out of sales. */
    failed: number;
}

export interface GroupedTotals extends SalesTotals {
    key: string;
    label: string | null;
    email?: string | null;
}

export interface DailyPoint {
    date: string;
    sales: number;
    bookings: number;
}

export interface DashboardResult {
    period: DashboardPeriod;
    currency: string;
    totals: SalesTotals;
    byBranch: GroupedTotals[];
    byStaff: GroupedTotals[];
    daily: DailyPoint[];
    /** Airlines that appear in the period, for the filter. */
    airlines: string[];
    filters: { airline: string | null; officeId: string | null };
    generatedAt: string;
}

export interface StaffBookingRow {
    bookingId: string;
    pnr: string | null;
    origin: string | null;
    destination: string | null;
    airlineCode: string | null;
    travelDate: string | null;
    passengerCount: number | null;
    amount: number | null;
    currency: string;
    paymentChannel: string | null;
    paymentStatus: string;
    ticketingStatus: string;
    cancellationStatus: string;
    /** The booking's own state. 'expired' means it ran out of time unpaid. */
    status: string;
    createdAt: string;
}

export interface StaffDetail {
    staff: {
        uid: string;
        email: string;
        displayName: string | null;
        mobile: string | null;
        officeId: string | null;
        staffStatus: string | null;
        role: string;
    };
    limit: LimitUsage;
    bookings: StaffBookingRow[];
    generatedAt: string;
}

export interface DashboardQuery {
    period?: DashboardPeriodName;
    from?: string;
    to?: string;
    airline?: string;
    officeId?: string;
}

export async function getDashboard(query: DashboardQuery = {}): Promise<DashboardResult> {
    const res = await apiClient.get("/api/admin/dashboard", { params: query });
    return res.data;
}

export async function getDashboardStaffList(): Promise<
    { uid: string; email: string; displayName: string | null; officeId: string | null; staffStatus: string | null }[]
> {
    const res = await apiClient.get("/api/admin/dashboard/staff");
    return res.data?.staff ?? [];
}

export async function getStaffDetail(uid: string): Promise<StaffDetail> {
    const res = await apiClient.get(`/api/admin/dashboard/staff/${encodeURIComponent(uid)}`);
    return res.data;
}

// ---------------------------------------------------------------------------
// Permission groups
// Backend: /admin/groups, /admin/permission-modules
// ---------------------------------------------------------------------------

export type ModuleKey =
    | "dashboard" | "staff" | "groups" | "branches" | "approvals"
    | "limits" | "bookings" | "passengers" | "markup" | "content" | "reports" | "settings";

export interface ModuleDefinition {
    key: ModuleKey;
    label: string;
    description: string;
    manageDescription: string;
    /** Granting this hands over the ability to widen anybody's access. */
    governsAccess?: boolean;
}

export interface ModulePermission { view: boolean; manage: boolean }
export type PermissionSet = Partial<Record<ModuleKey, ModulePermission>>;

export interface PermissionGroup {
    id: number;
    name: string;
    description: string | null;
    permissions: PermissionSet;
    memberCount?: number;
    createdAt: string;
    updatedAt: string;
}

export interface GroupMember {
    uid: string;
    email: string;
    displayName: string | null;
    addedBy: string | null;
    addedAt: string;
}

export interface AssignablePerson {
    uid: string;
    email: string;
    displayName: string | null;
    staffStatus: string | null;
    effectiveRole: string;
}

export async function getPermissionModules(): Promise<ModuleDefinition[]> {
    const res = await apiClient.get("/api/admin/permission-modules");
    return res.data?.modules ?? [];
}

export async function getPermissionGroups(): Promise<PermissionGroup[]> {
    const res = await apiClient.get("/api/admin/groups");
    return res.data?.groups ?? [];
}

export async function getPermissionGroup(
    id: number,
): Promise<{ group: PermissionGroup; members: GroupMember[] }> {
    const res = await apiClient.get(`/api/admin/groups/${id}`);
    return res.data;
}

export async function getAssignableStaff(): Promise<AssignablePerson[]> {
    const res = await apiClient.get("/api/admin/groups/assignable");
    return res.data?.staff ?? [];
}

export async function createPermissionGroup(input: {
    name: string; description?: string; permissions?: PermissionSet;
}): Promise<PermissionGroup> {
    const res = await apiClient.post("/api/admin/groups", input);
    return res.data?.group;
}

export async function updatePermissionGroup(
    id: number,
    input: { name?: string; description?: string | null; permissions?: PermissionSet },
): Promise<PermissionGroup> {
    const res = await apiClient.put(`/api/admin/groups/${id}`, input);
    return res.data?.group;
}

export async function deletePermissionGroup(
    id: number,
): Promise<{ name: string; membersReleased: number }> {
    const res = await apiClient.delete(`/api/admin/groups/${id}`);
    return res.data;
}

/** Returns the group's members afterwards, plus a note when it changes nothing. */
export async function addGroupMember(
    id: number, uid: string,
): Promise<{ members: GroupMember[]; note: string | null }> {
    const res = await apiClient.post(`/api/admin/groups/${id}/members`, { uid });
    return res.data;
}

export async function removeGroupMember(id: number, uid: string): Promise<GroupMember[]> {
    const res = await apiClient.delete(`/api/admin/groups/${id}/members/${encodeURIComponent(uid)}`);
    return res.data?.members ?? [];
}
